import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PLAID_INTEGRATION_VERSION_V2,
  PLAID_LINK_ERROR,
  PLAID_LINK_INITIATED,
  PLAID_MAX_LINK_ATTEMPTS_REACHED,
} from 'shared/analytics/events/externalAccount';
import { useAnalytics } from 'shared/hooks/analytics/useAnalytics';
import { claimPlaidAnalytics } from 'shared/hooks/usePlaidV2/utils/plaidAnalyticsOwnership';

import { PlaidIntent, PlaidIntentTypes, PlaidOpenResult } from './types';
import useGetPlaidTokens, { FetchTokenResult } from './useGetPlaidTokens';
import usePlaidV2Callbacks from './usePlaidV2Callbacks';
import usePlaidV2Platform from './usePlaidV2Platform';

const MAX_RETRY_ATTEMPTS = 3;

const getIntentKey = (intent: PlaidIntentTypes) => {
  switch (intent.intent) {
    case PlaidIntent.Link:
      return `link:${intent.product}:${intent.entryPoint ?? 'none'}`;
    case PlaidIntent.ReLink:
      return `relink:${intent.accountId}`;
    case PlaidIntent.CraUpgrade:
      return `craUpgrade:${intent.accountId}`;
    case PlaidIntent.Wait:
    default:
      return 'wait';
  }
};

/**
 * Orchestrates the Plaid V2 flow for the current intent.
 *
 * The hook automatically prepares a token once per unique non-`Wait` intent,
 * wires Plaid success/exit callbacks, exposes when the flow is ready to open,
 * and owns cleanup for both token and native Plaid state.
 *
 * Retry state is scoped to the current intent key. Changing to a different
 * intent resets the auto-prepare guard and retry tracking for the new flow.
 * Calling `openPlaid()` delegates to the platform implementation and then
 * resets the prepared token/native state after the open attempt resolves.
 *
 *  Start with the `Wait` intent, then switch to the desired intent after the
 * component mounts.
 *
 * @param intent The Plaid flow to prepare. Pass `Wait` to keep the hook idle.
 * @param options `sourceName` is the approved analytics source for the surface
 * launching Plaid. Every launch site must supply one.
 * @returns An object containing:
 * - `openPlaid`: Opens the prepared Plaid flow and then clears hook-managed
 *   Plaid state once the platform call resolves.
 * - `retry`: Re-fetches a token for the current intent when retries are still
 *   allowed. Becomes `undefined` after the retry limit is reached.
 * - `ready`: `true` when token preparation is complete and the platform hook is
 *   ready to open Plaid.
 * - `fetching`: `true` while the hook is preparing or retrying the token.
 * - `error`: The current token error, or a max-attempts error once retries are
 *   exhausted.
 * - `reset`: Clears hook-managed retry, token, and native Plaid state.
 */
type UsePlaidV2Options = {
  sourceName?: string;
};

const usePlaidV2 = (intent: PlaidIntentTypes, options?: UsePlaidV2Options) => {
  const { logEvent } = useAnalytics();
  const sourceName = options?.sourceName;
  // autoFetchedIntentKeyRef tracks the intent key for which the hook has already auto-fetched a token.
  // This ensures that we only auto-fetch once per unique intent, and prevents auto-fetching when switching back to a previously used intent.
  const autoFetchedIntentKeyRef = useRef<string | null>(null);
  const currentIntentKey = getIntentKey(intent);
  const currentIntentKeyRef = useRef(currentIntentKey);

  const [retryAttempts, setRetryAttempts] = useState(0);
  const { t } = useTranslation('plaidV2Strings', {
    keyPrefix: 'errors',
  });

  const {
    fetching,
    error: tokenError,
    fetchToken,
    linkReferenceId,
    reset: resetTokens,
    token,
  } = useGetPlaidTokens(intent);

  const hasReachedRetryLimit = retryAttempts >= MAX_RETRY_ATTEMPTS;
  const shouldShowMaxAttemptsError = hasReachedRetryLimit && !!tokenError;

  const maxAttemptsError: {
    title: string;
    name: string;
    message: string;
  } | null = shouldShowMaxAttemptsError
    ? {
        name: 'MaxRetryAttemptsExceeded',
        title: t('maxAttemptsTitle'),
        message: t('maxAttemptsMessage'),
      }
    : null;

  const callbacks = usePlaidV2Callbacks({
    intent,
    linkReferenceId,
    refetchTokens: fetchToken,
    sourceName,
  });

  const {
    openPlaid: openPlatformPlaid,
    ready: sdkReady,
    reset: resetPlaid,
  } = usePlaidV2Platform(intent, token, callbacks);

  const isWaitIntent = currentIntentKey === 'wait';

  useEffect(() => {
    currentIntentKeyRef.current = currentIntentKey;
  }, [currentIntentKey]);

  // Auto-fetch token when intent changes from Wait to a real intent.
  // Or when the specific intent changes (e.g. different product entry point for Link intent, or different account id for ReLink/CraUpgrade intent).
  useEffect(() => {
    if (isWaitIntent) {
      autoFetchedIntentKeyRef.current = null;
      resetTokens();
      resetPlaid();
      return;
    }

    if (
      autoFetchedIntentKeyRef.current !== null &&
      autoFetchedIntentKeyRef.current !== currentIntentKey
    ) {
      resetTokens();
    }

    if (fetching || autoFetchedIntentKeyRef.current === currentIntentKey) {
      return;
    }

    let isStaleRequest = false;

    const handleFetchToken = async () => {
      resetTokens();
      autoFetchedIntentKeyRef.current = currentIntentKey;

      setRetryAttempts(0);

      try {
        const result = await fetchToken();

        if (result.status === 'aborted') {
          // Only clear the ref if this aborted fetch still owns it.
          // A newer intent may already have started and replaced the ref.
          if (autoFetchedIntentKeyRef.current === currentIntentKey) {
            autoFetchedIntentKeyRef.current = null;
          }
          return;
        }

        if (isStaleRequest) {
          return;
        }

        if (result.status === 'failed') {
          logEvent(PLAID_LINK_ERROR, {
            intent: intent.intent,
            error_type: 'network',
            error_step: 'token_fetch',
            entry_point: 'entryPoint' in intent ? intent.entryPoint : undefined,
            source_name: sourceName,
            plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
          });
          setRetryAttempts((previous) => previous + 1);
        }
      } catch (_error) {
        if (isStaleRequest) {
          return;
        }

        setRetryAttempts((previous) => previous + 1);
      }
    };

    void handleFetchToken();

    return () => {
      isStaleRequest = true;
    };
  }, [
    currentIntentKey,
    fetchToken,
    isWaitIntent,
    logEvent,
    resetPlaid,
    resetTokens,
    fetching,
    intent,
    sourceName,
  ]);

  const ready = sdkReady && !!token;

  const reset = useCallback(() => {
    autoFetchedIntentKeyRef.current = null;
    setRetryAttempts(0);
    resetTokens();
    resetPlaid();
  }, [resetPlaid, resetTokens]);

  useEffect(() => {
    return reset;
  }, [reset]);

  const openPlaid = useCallback(async () => {
    logEvent(PLAID_LINK_INITIATED, {
      intent: intent.intent,
      product: 'product' in intent ? intent.product : undefined,
      entry_point: 'entryPoint' in intent ? intent.entryPoint : undefined,
      source_name: sourceName,
      plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
    });

    let result: PlaidOpenResult;
    const unclaimPlaidAnalytics = claimPlaidAnalytics();

    try {
      result = await openPlatformPlaid();
    } catch (error) {
      result = {
        status: 'error',
        error: {
          name: error instanceof Error ? error.name : 'UnknownError',
          message:
            error instanceof Error
              ? error.message
              : 'Unexpected Plaid open error',
        },
      };
    } finally {
      unclaimPlaidAnalytics();
    }

    if (result.status === 'success' || result.status === 'error') {
      reset();
    }
    return result;
  }, [intent, logEvent, openPlatformPlaid, reset, sourceName]);

  const retry = useCallback(async () => {
    if (hasReachedRetryLimit) {
      logEvent(PLAID_MAX_LINK_ATTEMPTS_REACHED, {
        intent: intent.intent,
        entry_point: 'entryPoint' in intent ? intent.entryPoint : undefined,
        source_name: sourceName,
        plaidIntegrationVersion: PLAID_INTEGRATION_VERSION_V2,
      });
      return;
    }
    if (fetching) {
      return;
    }

    resetTokens();

    let result: FetchTokenResult;

    try {
      result = await fetchToken();
    } catch (_error) {
      setRetryAttempts((previous) => previous + 1);
      return;
    }

    if (
      currentIntentKeyRef.current !== currentIntentKey ||
      result.status === 'aborted' ||
      result.status === 'skipped'
    ) {
      return;
    }

    if (result.status === 'failed') {
      setRetryAttempts((previous) => previous + 1);
      return;
    }

    if (result.status === 'success') {
      setRetryAttempts(0);
    }
  }, [
    currentIntentKey,
    fetchToken,
    fetching,
    hasReachedRetryLimit,
    intent,
    logEvent,
    resetTokens,
    sourceName,
  ]);

  return {
    openPlaid,
    retry: hasReachedRetryLimit ? undefined : retry,
    ready,
    fetching,
    error: maxAttemptsError || tokenError,
    reset,
  };
};

export default usePlaidV2;
